import './assets/css/styles.module.css';
import frame from './AppFrame.module.css';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { setAccessToken } from './utils/authToken';
import { getProfile } from './apis/api';

import Header from './components/Header/Header.jsx'
import NavBar from './components/NavBar/NavBar.jsx'
import Landing from './pages/Landing/Landing';
import Home from './pages/OnBoarding/Home/Home';
import ChatList from './pages/Chatting/ChatList/ChatList';
import ChatRoom from './pages/Chatting/ChatRoom/ChatRoom';
import ChatJoinPage from './pages/Chatting/ChatJoin/ChatJoinPage';
import AiChat from './pages/Chatting/AiChat/AiChat';
import Trip from './pages/Trip/Trip';
import ProfileSetup from './pages/OnBoarding/ProfileSetup/ProfileSetup';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';

import AuthChoice from './pages/AuthChoice/AuthChoice';
import Signup from './pages/Signup/Signup';
import Login from './pages/Login/Login';

import Dogeja from './pages/Dogeja/Dogeja';
import MyPage from './pages/MyPage/MyPage';
import TripStyleEdit from './pages/MyPage/TripStyleEdit';

import MapPage from './pages/MyPage/MapPage';
import ChatSearchPage from './pages/Chatting/Search/ChatSearchPage';
import Search from './pages/Search/Search';
import IntroPage from './pages/Intro/IntroPage';
import HelpChatWidget from './components/HelpChat/HelpChatWidget';
import useAuthStore from './stores/useAuthStore';
import useDataStore from './stores/useDataStore';

function App() {
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();
    const username = useAuthStore((s) => s.user?.username || null);
    const setActiveTripPlansUser = useDataStore((s) => s.setActiveTripPlansUser);
    const isChatWizardActive = useDataStore((s) => s.isChatWizardActive);
    // Trigger HMR

    // Keep local trip plan cache scoped to the current user
    useEffect(() => {
        if (typeof setActiveTripPlansUser === 'function') {
            setActiveTripPlansUser(username);
        }
    }, [username, setActiveTripPlansUser]);

    // Global OAuth Token Handling
    useEffect(() => {
        // Email verification also uses ?token=... in the URL.
        // Do not treat that token as an OAuth access token.
        if (pathname === '/verify-email') return;

        const searchParams = new URLSearchParams(search);

        // Some providers/backends may return token in hash fragment: #token=... or #access_token=...
        const hashString = (hash || '').startsWith('#') ? (hash || '').slice(1) : (hash || '');
        const hashParams = new URLSearchParams(hashString);

        const tokenFromUrl =
            searchParams.get('token') ||
            searchParams.get('access_token') ||
            searchParams.get('accessToken') ||
            searchParams.get('authorization') ||
            hashParams.get('token') ||
            hashParams.get('access_token') ||
            hashParams.get('accessToken') ||
            hashParams.get('authorization');

        // Refresh Token도 URL에서 추출
        const refreshTokenFromUrl =
            searchParams.get('refreshToken') ||
            searchParams.get('refresh_token') ||
            hashParams.get('refreshToken') ||
            hashParams.get('refresh_token');

        if (tokenFromUrl) {
            console.log("Global Token Handler: Found token in URL");
            // setAccessToken에 refreshToken도 함께 전달
            setAccessToken(tokenFromUrl, 'Social User', refreshTokenFromUrl);

            // OAuth도 일반 로그인과 동일하게: 프로필 미완료면 프로필 설정으로 유도
            let cancelled = false;
            (async () => {
                try {
                    const profile = await getProfile();
                    if (cancelled) return;

                    const isIncomplete = !profile?.nickname || !profile?.birthDate;
                    const nextPath = isIncomplete ? '/profile-setup' : (pathname || '/home');

                    // 토큰 쿼리/해시 제거 + 목적지로 이동
                    navigate(nextPath, { replace: true });
                } catch (e) {
                    if (cancelled) return;
                    // 프로필 조회 실패 시에도 토큰만 URL에서 제거하고 현재 페이지로
                    navigate(pathname, { replace: true });
                }
            })();

            return () => {
                cancelled = true;
            };
        }
    }, [search, hash, pathname, navigate]);
    // Merge hideHeader conditions: chatlist, chatroom, landing(/landing), start page, login page, dogeja page, mypage, edit-style, intro
    const hideHeader = pathname === '/chatlist' || pathname === '/chatroom' || pathname === '/ai-chat' || pathname === '/landing' || pathname === '/start' || pathname === '/dogeja' || pathname === '/mypage' || pathname === '/mypage/edit-style' || pathname === '/mypage/map' || pathname === '/onboarding' || pathname === '/profile-setup' || pathname === '/verify-email' || pathname === '/search' || pathname === '/chat/search' || pathname === '/intro'; // Updated hideHeader
    const isChatRoom = pathname === '/chatroom' || pathname === '/ai-chat';
    const hideHelpWidget =
        isChatRoom ||
        pathname === '/mypage/edit-style' ||
        pathname === '/mypage/map' ||
        (pathname === '/chatlist' && isChatWizardActive);

    // Merge hideNav conditions: dogeja page, landing(/landing), start, login, signup, mypage, edit-style, intro
    // Note: Incoming changed hideNav logic to use variable. We will combine them.
    const hideNav = pathname === '/dogeja' || pathname === '/landing' || pathname === '/start' || pathname === '/login' || pathname === '/signup' || pathname === '/onboarding' || pathname === '/profile-setup' || pathname === '/verify-email' || pathname === '/mypage' || pathname === '/mypage/edit-style' || pathname === '/mypage/map' || pathname === '/search' || pathname === '/chat/search' || pathname === '/intro'; // Updated hideNav

    return (
        <div className={frame.appShell}>
            <div className={frame.device}>
                <div className={`${frame.background} ${isChatRoom ? frame.backgroundChat : ''}`} />
                <div className={`${frame.scrollArea} ${pathname === '/landing' ? frame.noPadding : ''}`}>
                    {!hideHeader && <Header />}
                    <Routes>
                        <Route path="/" element={<Navigate to="/intro" replace />} />
                        <Route path="/intro" element={<IntroPage />} />
                        <Route path="/landing" element={<Landing />} />
                        <Route path="/home" element={<Home />} />
                        <Route path="/chatlist" element={<ChatList />} />
                        <Route path="/chatroom" element={<ChatRoom />} />
                        <Route path="/chat/join" element={<ChatJoinPage />} />
                        <Route path="/ai-chat" element={<AiChat />} />

                        <Route path="/trip" element={<Trip />} />
                        <Route path="/profile-setup" element={<ProfileSetup />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />

                        {/* Auth Routes */}
                        <Route path="/start" element={<AuthChoice />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />

                        {/* New Routes from Main */}
                        <Route path="/dogeja" element={<Dogeja />} />
                        <Route path="/search" element={<Search />} />
                        <Route path="/chat/search" element={<ChatSearchPage />} />
                        <Route path="/mypage" element={<MyPage />} />
                        <Route path="/mypage/edit-style" element={<TripStyleEdit />} />
                        <Route path="/mypage/map" element={<MapPage />} />
                    </Routes>
                </div>
                {!isChatRoom && !hideNav && <NavBar />}
                <img src={require('./assets/pic/Home Indicator.png')} alt="" className={frame.homeIndicator} />
                {!hideHelpWidget && <HelpChatWidget hasNav={!isChatRoom && !hideNav} />}
            </div>
        </div>
    );
}

export default App;
